import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { assetTypeStatusSortFn, type AssetTypeStatus, type Stored } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { assetTypeStatusFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import {
	AssetTypeStatusSelectGroup,
	type AssetTypeStatusSelectGroupProps,
} from "../MenuSelectGroups/AssetTypeStatusSelectGroup"

type AssetTypeStatusSelectProps = Omit<MenuRootProps, "children"> &
	Omit<MenuClickTriggerProps, "children"> &
	MenuVariantProps &
	AssetTypeStatusSelectGroupProps & {
		children: ReactElement
	}

export const AssetTypeStatusSelect = memo<AssetTypeStatusSelectProps>((props) => {
	const {
		// Trigger props
		children,
		disabled = false,
		// MenuContent props
		size,
		radius,
		variant,
		accentColor,
		// Status props
		assetTypeStatuses,
		required,
		defaultValue,
		value,
		onValueChange,
		// Rest props
		...rest
	} = props

	const sortedAssetTypeStatuses = useSort(assetTypeStatuses, assetTypeStatusSortFn)

	const [filteredStatuses, filterValue, setFilterValue] = useTextFilter<Stored<AssetTypeStatus>>(
		sortedAssetTypeStatuses,
		assetTypeStatusFilterCallback,
	)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} variant={variant} accentColor={accentColor}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{filteredStatuses.length > 0 && <Menu.Separator />}
				<Menu.Scroll>
					{required ? (
						<AssetTypeStatusSelectGroup
							assetTypeStatuses={filteredStatuses}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					) : (
						<AssetTypeStatusSelectGroup
							assetTypeStatuses={filteredStatuses}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
AssetTypeStatusSelect.displayName = "AssetTypeStatusSelect"
